/// <amd-module name="WorkItemsHub/DataProviders/WorkItemsGridDataProvider" />
import { IColumn } from "OfficeFabric/DetailsList";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { findIndex, toDictionary } from "WorkItemsHub/Utils/Array";
import { equals } from "VSS/Core/Util/String";
import { IWorkItemsGridData, IWorkItemsGridRow } from "WorkItemsHub/DataContracts/IWorkItemsGridData";
import { WorkItemsHubColumnOption, WorkItemsHubData, WorkItemsHubSortOption } from "WorkItemsHub/Generated/Contracts";
import { WorkItemsHubFilterDataSource } from "WorkItemsHub/Stores/WorkItemsHubFilterDataSource";
import { IWorkItemsGridColumnFactory, WorkItemsGridColumnFactory } from "WorkItemsHub/Utils/WorkItemsGridColumnFactory";
import { OnOpenWorkItemHandler } from "Utils/NavigationUtils";
import { getFieldFriendlyName } from "WorkItemsHub/Utils/WorkItemsHubDataUtils";
import { IVssPageContext } from "VSS/Platform/Context";

/**
 * Provides data for the grid (based on the hub data and a column factory).
 */
export namespace WorkItemsGridDataProvider {
    export function buildColumns(
        pageContext: IVssPageContext,
        projectName: string,
        hubData: WorkItemsHubData,
        sortOptions: WorkItemsHubSortOption[],
        gridColumnFactory: IWorkItemsGridColumnFactory,
        friendlyStartDateTime: Date,
        onOpenWorkItem: OnOpenWorkItemHandler
    ): IColumn[] {
        const columnMap = _getColumnMap(hubData);
        const columnsToShow: WorkItemsHubColumnOption[] = _getColumnsToShow(hubData);

        return columnsToShow.map(column =>
            gridColumnFactory.create(
                pageContext,
                projectName,
                column.fieldReferenceName,
                getFieldFriendlyName(column.fieldReferenceName, hubData.processSettings),
                columnMap,
                friendlyStartDateTime,
                onOpenWorkItem,
                _isColumnSorted(column.fieldReferenceName, sortOptions),
                _isColumnSortedDescending(column.fieldReferenceName, sortOptions),
                column.width
            )
        );
    }

    export function getData(hubData: WorkItemsHubData, isFiltered: boolean): IWorkItemsGridData {
        if (!hubData || !hubData.processSettings || !hubData.processSettings.fieldReferenceNames || !hubData.fieldValues) {
            return null;
        }

        const stateIndex = findIndex(hubData.processSettings.fieldReferenceNames, (fieldRefName: string) =>
            equals(fieldRefName, CoreFieldRefNames.State, true)
        );

        return hubData.fieldValues.map((securedData, i) => {
            const values = securedData.data;
            const id: number = values[0];
            const state: string = values[stateIndex];
            const isCompleted: boolean =
                findIndex(hubData.processSettings.doneStateNames, (stateName: string) => equals(stateName, state, true)) >= 0;

            // Per guidance on https://reactjs.org/docs/lists-and-keys.html#keys, we should avoid using index as key; however, our data is static and we benefit
            // noticeably as we have many rows and many elements on each.
            return { key: i.toString(10), id, isCompleted, values } as IWorkItemsGridRow;
        });
    }

    function _getColumnMap(hubData: WorkItemsHubData): { [key: string]: number } {
        return toDictionary(hubData.processSettings.fieldReferenceNames, fieldReferenceName => fieldReferenceName, (_, index) => index);
    }

    function _getColumnsToShow(hubData: WorkItemsHubData): WorkItemsHubColumnOption[] {
        let columns: WorkItemsHubColumnOption[];

        if (hubData.userSettings) {
            const columnSettings = hubData.userSettings.columnSettings;
            if (columnSettings && columnSettings.columnOptions && columnSettings.columnOptions.length > 0) {
                columns = hubData.userSettings.columnSettings.columnOptions;
            }
        }

        if (!columns) {
            // Remove work item type and my activity details from default column list
            const filteredDefaultFields = hubData.processSettings.fieldReferenceNames.filter(
                f => WorkItemsHubFilterDataSource.ExcludedFieldsFromDefaultColumns.indexOf(f) < 0
            );
            columns = filteredDefaultFields.map(
                fieldName =>
                    ({
                        fieldReferenceName: fieldName,
                        width: WorkItemsGridColumnFactory.getDefaultColumnWidth(fieldName)
                    } as WorkItemsHubColumnOption)
            );
        }

        return columns;
    }

    function _isColumnSorted(fieldReferenceName: string, sortOptions: WorkItemsHubSortOption[]): boolean {
        let sorted = false;
        if (sortOptions) {
            sortOptions.forEach(option => {
                if (equals(option.fieldReferenceName, fieldReferenceName, true)) {
                    sorted = true;
                }
            });
        }

        return sorted;
    }

    function _isColumnSortedDescending(fieldReferenceName: string, sortOptions: WorkItemsHubSortOption[]): boolean {
        let sorted = false;
        sortOptions.forEach(option => {
            if (equals(option.fieldReferenceName, fieldReferenceName, true)) {
                sorted = !option.isAscending;
            }
        });

        return sorted;
    }
}
